@if (isVisible()) {
<div class="dep-overlay">
  <div
    class="dep-modal"
    role="dialog"
    aria-modal="true"
    aria-labelledby="dep-title"
    (click)="$event.stopPropagation()">
    <div class="dep-rail"><span class="dep-rail-fill" [style.width]="railWidth()"></span></div>

    <!-- shared header (updates per step) -->
    <div class="dep-head">
      <div class="dep-head-left">
        <div class="dep-badge" [class.warn]="headerBadge() === 'confirm'">
          @switch (headerBadge()) {
            @case ('vendor') {
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="7" width="18" height="13" rx="2"/><path d="M8 7V5a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><line x1="3" y1="12" x2="21" y2="12"/></svg>
            }
            @case ('customer') {
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="9"/><path d="M12 7v10M9.5 9.5c0-1.1 1.1-2 2.5-2s2.5.7 2.5 1.8c0 2.2-5 1.6-5 4 0 1.1 1.1 2 2.5 2s2.5-.9 2.5-2"/></svg>
            }
            @case ('confirm') {
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            }
          }
        </div>
        <div>
          <div class="dep-dots">
            <span class="dep-dot" [class.on]="!onSecondDot()"></span>
            <span class="dep-dot" [class.on]="onSecondDot()"></span>
          </div>
          <h1 class="dep-title" id="dep-title">{{ headerTitle() }}</h1>
          <p class="dep-sub">{{ headerSub() }}</p>
        </div>
      </div>
      <button class="dep-close" type="button" title="Close" aria-label="Close" (click)="onClose()">✕</button>
    </div>

    <div class="dep-stage">

      @if (isLoading()) {
        <div class="dep-view">
          <p class="dep-question">Loading vendor information…</p>
        </div>
      } @else if (loadError()) {
        <div class="dep-view">
          <div class="dep-status-warn red show">
            <span>{{ loadError() }}</span>
          </div>
          <div class="dep-foot">
            <button class="dep-btn ghost" type="button" (click)="onClose()">Close</button>
          </div>
        </div>
      } @else {

      <!-- ========== STEP 0: PICK VENDORS (group-wide entry, 2+ vendors) ========== -->
      @if (step() === 'pick') {
        <section class="dep-view">
          <p class="dep-question">This customer estimate combines {{ allVendors().length }} vendors. Which ones are we setting a deposit for?</p>

          <ul class="dep-pick-list">
            @for (vendor of allVendors(); track vendor.jobVendorKey) {
              <li>
                <label class="dep-pick-row" [class.picked]="isVendorPicked(vendor.jobVendorKey)">
                  <input
                    type="checkbox"
                    class="dep-pick-box"
                    [checked]="isVendorPicked(vendor.jobVendorKey)"
                    (change)="toggleVendorPick(vendor.jobVendorKey)"
                  />
                  <span class="dep-pick-main">
                    <span class="dep-pick-name">{{ vendor.vendorName }}</span>
                    <span class="dep-pick-meta">
                      Estimate {{ formatMoney(vendor.vendorEstimateTotal) }}
                      @if (vendor.existingDepositAmount > 0) {
                        · <span class="dep-pick-existing">deposit {{ formatMoney(vendor.existingDepositAmount) }} already set</span>
                      }
                    </span>
                  </span>
                </label>
              </li>
            }
          </ul>

          <p class="dep-hint">Unchecked vendors are skipped — their deposits are left exactly as they are.</p>

          <div class="dep-foot">
            <button class="dep-btn ghost" type="button" (click)="onClose()">Cancel</button>
            <div class="dep-spacer"></div>
            <button class="dep-btn-primary" type="button" [disabled]="!canProceedPick()" (click)="onPickNext()">
              Next{{ pickedVendorKeys().size > 0 ? ' (' + pickedVendorKeys().size + ')' : ' (no vendor deposit)' }}
            </button>
          </div>
        </section>
      }

      <!-- ========== STEP 1: VENDOR (one page per participating vendor) ========== -->
      @if (step() === 'vendor' && currentVendorPage(); as page) {
        <section class="dep-view">
          @if (isMultiVendor()) {
            <p class="dep-page-indicator">Vendor {{ vendorPageIndex() + 1 }} of {{ vendorPages().length }}</p>
          }

          <div class="dep-ctx">
            <div class="dep-avatar">{{ vendorInitials() }}</div>
            <div>
              <p class="dep-ctx-label">Vendor</p>
              <p class="dep-ctx-name">{{ page.info.vendorName }}</p>
            </div>
          </div>

          <p class="dep-question">Is this vendor requiring an upfront deposit, and are we agreeing to pay them a deposit?</p>

          <div class="dep-segmented">
            <button class="dep-seg-btn" type="button" [class.active-yes]="page.choice === 'yes'" (click)="selectYes()">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
              Yes
            </button>
            <button class="dep-seg-btn" type="button" [class.active-no]="page.choice === 'no'" (click)="selectNo()">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              No
            </button>
          </div>

          <div class="dep-amounts" [class.show]="page.choice === 'yes'">
            <div class="dep-field">
              <p class="dep-fld-label">Deposit Amount</p>
              <div class="dep-input-wrap">
                <span class="dep-prefix">$</span>
                <input
                  type="text"
                  class="dep-amt with-prefix"
                  placeholder="0.00"
                  inputmode="decimal"
                  [disabled]="page.choice !== 'yes'"
                  [ngModel]="page.dollar"
                  (ngModelChange)="setVendorDollar($event)"
                />
              </div>
              <p class="dep-hint">Only $ amount allowed in this field</p>
            </div>
            <div class="dep-field">
              <p class="dep-fld-label">Deposit Percent</p>
              <div class="dep-input-wrap">
                <input
                  type="text"
                  class="dep-amt with-suffix"
                  placeholder="0"
                  [disabled]="page.choice !== 'yes'"
                  [ngModel]="page.percent"
                  (ngModelChange)="setVendorPercent($event)"
                />
                <span class="dep-suffix">%</span>
              </div>
              <p class="dep-hint">Only % amount allowed in this field</p>
            </div>
          </div>

          @if (page.showWarning) {
            <div class="dep-status-warn red show">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>{{ vendorWarningText() }}</span>
            </div>
            <div class="dep-field dep-override-field">
              <p class="dep-fld-label">Override Reason <span class="dep-req">(Required)</span></p>
              <textarea
                class="dep-reason"
                [ngModel]="page.overrideReason"
                (ngModelChange)="setVendorOverrideReason($event)"
              ></textarea>
            </div>
          }

          <div class="dep-foot">
            @if (page.showWarning) {
              <div class="dep-footer-warning show">
                <button class="dep-btn danger" type="button" (click)="reduceVendorDeposit()">Reduce Deposit Amount</button>
                <button class="dep-btn-primary" type="button" [disabled]="!page.overrideReason.trim()" (click)="overrideVendorDeposit()">Override &amp; Continue</button>
              </div>
            } @else {
              <div class="dep-footer-default">
                <button class="dep-btn ghost" type="button" (click)="backFromVendorPage()">{{ isFirstVendorPage() ? 'Cancel' : 'Back' }}</button>
                <button class="dep-btn-primary" type="button" [disabled]="!canProceedVendor()" (click)="onVendorNext()">Next</button>
              </div>
            }
          </div>
        </section>
      }

      <!-- ========== STEP 2: CUSTOMER AMOUNT ========== -->
      @if (step() === 'customer') {
        <section class="dep-view">
          <p class="dep-question">Please enter the Customer Deposit Amount</p>

          <div class="dep-amounts show">
            <div class="dep-field">
              <p class="dep-fld-label">Deposit Amount</p>
              <div class="dep-input-wrap">
                <span class="dep-prefix">$</span>
                <input
                  type="text"
                  class="dep-amt with-prefix"
                  placeholder="0.00"
                  inputmode="decimal"
                  [ngModel]="customerDollar()"
                  (ngModelChange)="onCustomerDollarChange($event)"
                />
              </div>
              <p class="dep-hint">Only $ amount allowed in this field</p>
            </div>
            <div class="dep-field">
              <p class="dep-fld-label">Deposit Percent</p>
              <div class="dep-input-wrap">
                <input
                  type="text"
                  class="dep-amt with-suffix"
                  placeholder="Only % Amount"
                  [ngModel]="customerPercent()"
                  (ngModelChange)="onCustomerPercentChange($event)"
                />
                <span class="dep-suffix">%</span>
              </div>
              <p class="dep-hint">Only % amount allowed in this field</p>
            </div>
          </div>

          @if (showExistingCustomerNotice()) {
            <div class="dep-status-warn blue show">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>
              <span>{{ existingCustomerNoticeText() }}</span>
            </div>
          }

          @if (showCustomerWarning()) {
            <div class="dep-status-warn red show">
              <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <span>{{ customerWarningText() }}</span>
            </div>
            @if (!exceedsCustomerEstimateTotal()) {
              <div class="dep-field dep-override-field">
                <p class="dep-fld-label">Override Reason <span class="dep-req">(Required)</span></p>
                <textarea
                  class="dep-reason"
                  [ngModel]="customerOverrideReason()"
                  (ngModelChange)="customerOverrideReason.set($event)"
                ></textarea>
              </div>
            }
          }

          @if (saveError()) {
            <div class="dep-status-warn red show"><span>{{ saveError() }}</span></div>
          }

          <div class="dep-foot">
            <button class="dep-btn" type="button" (click)="backToVendorStep()">Go back and edit Vendor Deposit Amount</button>
            <div class="dep-spacer"></div>
            <button class="dep-btn amber" type="button" (click)="customerNotNeeded()">Customer Deposit Not Needed</button>
            <button
              class="dep-btn-primary"
              type="button"
              [disabled]="!canSaveCustomer() || isSaving() || exceedsCustomerEstimateTotal() || (showCustomerWarning() && !customerOverrideReason().trim())"
              (click)="saveCustomerDeposit()">
              {{ isSaving() ? 'Saving…' : 'Save Customer Deposit' }}
            </button>
          </div>
        </section>
      }

      <!-- ========== STEP 3: NO-DEPOSIT CONFIRMATION ========== -->
      @if (step() === 'confirm') {
        <section class="dep-view">
          @if (noCustomerDepositReasonRequired()) {
            <p class="dep-confirm-head">Are you sure that you do not want to require a deposit from the customer since you will be paying an upfront deposit to the vendor?</p>

            <p class="dep-confirm-body">Change your choice or click 'Continue with No Customer Deposit' to continue and explain reason for not charging customer deposit and allowing vendor to be paid deposit here:</p>
          } @else {
            <p class="dep-confirm-head">Are you sure that you do not want to require a deposit from the customer?</p>
          }

          <div class="dep-field">
            <p class="dep-fld-label">Reason @if (noCustomerDepositReasonRequired()) { <span class="dep-req">(Required)</span> } @else { <span class="dep-req">(Optional)</span> }</p>
            <textarea
              class="dep-reason"
              [ngModel]="reason()"
              (ngModelChange)="reason.set($event)"
            ></textarea>
          </div>

          @if (saveError()) {
            <div class="dep-status-warn red show"><span>{{ saveError() }}</span></div>
          }

          <div class="dep-foot">
            <button class="dep-btn danger" type="button" (click)="requireCustomerDeposit()">Change your choice and Require Customer Deposit</button>
            <div class="dep-spacer"></div>
            <button class="dep-btn-primary" type="button" [disabled]="!canContinueNoDeposit() || isSaving()" (click)="continueNoCustomerDeposit()">
              {{ isSaving() ? 'Saving…' : 'Continue with No Customer Deposit' }}
            </button>
          </div>
        </section>
      }

      }
    </div>
  </div>
</div>
}
